import { ObjectId } from '@freightview/object-id'
import { useCallback } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import type { FullShipment } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'

import type { ApiQuoteRequestDTO } from '@/types'
import type { ApiQuoteRequestEquipmentDTO } from '@/types/api-dto'

const crStorageKey = 'customer-routed'

export function orderNumberFromRefNums(
  refNums?: Array<{ type: string; value: string }>,
) {
  return refNums?.find(refNum => refNum.type === 'other')?.value
}

const useCrLocalStorage = () => {
  return useLocalStorage<ApiQuoteRequestDTO | null>(crStorageKey, null, {
    initializeWithValue: true,
  })
}

export const useSaveCustomerRoutedDraft = () => {
  const [, setVal] = useCrLocalStorage()
  return (dto: ApiQuoteRequestDTO) => {
    setVal(dto)
  }
}

export const useCustomerRoutedDraft = () => {
  const [val] = useCrLocalStorage()
  if (val === null) return null
  const loads = draftToFullShipments(val)
  return loads.length ? loads : null
}

export const useClearCustomerRoutedDraft = () => {
  const [, , clearVal] = useCrLocalStorage()
  return useCallback(() => clearVal(), [clearVal])
}

export function draftToFullShipments(dto: ApiQuoteRequestDTO): FullShipment[] {
  const [origin, dest] = dto.locations
  if (!origin || !dest || !dto.equipment.length) return []

  const createdDate = fvDateUtc().toString()
  const quoteRequestId = new ObjectId().toHexString()
  const isMultiTruck = dto.equipment.length > 1

  return dto.equipment.map(equip =>
    buildDraftShipment({
      createdDate,
      dto,
      equip,
      isMultiTruck,
      originStopDate: origin.stopDate,
      quoteRequestId,
    }),
  )
}

type BuildDraftShipmentArgs = {
  createdDate: string
  dto: ApiQuoteRequestDTO
  equip: ApiQuoteRequestEquipmentDTO
  isMultiTruck: boolean
  originStopDate?: string | Date | null
  quoteRequestId: string
}

function buildDraftShipment({
  createdDate,
  dto,
  equip,
  isMultiTruck,
  originStopDate,
  quoteRequestId,
}: BuildDraftShipmentArgs): FullShipment {
  const pickupDate = noonUtc(originStopDate) || createdDate

  return {
    accountId: '',
    createdDate,
    equipment: {
      accessorials: equip.accessorials,
      alternateTypes: equip.alternateTypes,
      declaredValue: equip.declaredValue,
      declaredValueCurrency: equip.declaredValueCurrency,
      description: equip.description,
      isHazardous: equip.isHazardous,
      mode: equip.mode,
      type: equip.type,
      weight: equip.weight,
      weightUOM: equip.weightUOM,
    },
    isArchived: false,
    isFreightCollect: true,
    isLiveLoad: dto.isLiveLoad,
    isMultiTruck,
    items: equip.items ?? [],
    locations: dto.locations.map(loc => ({
      ...loc,
      stopDate: noonUtc(loc.stopDate),
    })),
    newMessageCount: 0,
    pickupDate,
    quotedByUserId: '',
    quoteRequestId,
    refNums: dto.orderNumber ? [{ type: 'other', value: dto.orderNumber }] : [],
    status: 'pending',
    workflow: dto.workflow,
    bol: { status: 'pending' },
    direction: 'outbound',
    documents: [],
    pickup: { status: 'pending' },
    source: 'app',
    schedulePickup: dto.schedulePickup ?? false,
    ...(dto.shares && { shares: dto.shares }),
    ...(dto.templateId && { templateId: dto.templateId }),
    ...(dto.glCode && { glCode: dto.glCode }),
    ...(dto.tags && { tags: dto.tags.map(name => ({ name })) }),
  } as FullShipment
}

function noonUtc(date?: string | Date | null) {
  if (!date) return date
  return fvDateUtc(date).set('hour', 12).toString()
}
